import React, { useState, useEffect, useRef } from 'react';
import { sessionService, SessionMetadata, ActiveSession } from '../../../services/SessionService';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import { firebaseAppointmentService, Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import { useAuth } from '../../../contexts/AuthContext';
import { TranscriptionSegment, TranscriptionSession } from '../../../services/TranscriptionService';
import { FilteredSegment, FilteringStats } from '../../../services/ContentFilteringService';
import AudioRecorder, { AudioRecorderHandle } from '../../AudioRecorder';
import { postSessionPipelineService, PostSessionResult } from '../../../services/PostSessionPipelineService';
import PostSessionReviewModal from '../../PostSessionReviewModal';
import NotificationTriggerService from '../../../services/NotificationTriggerService';
import VoiceInputButton from '../../VoiceInputButton';

interface ProviderSessionTabProps {
  preSelectedPatient?: Patient | null;
  preSelectedAppointment?: Appointment | null;
}

const ProviderSessionTab: React.FC<ProviderSessionTabProps> = ({ preSelectedPatient, preSelectedAppointment }) => {
  const { user } = useAuth();
  const providerId = user?.uid || '';

  // Session state
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [sessionMetadata, setSessionMetadata] = useState<SessionMetadata | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  // Appointment tracking
  const [currentAppointment, setCurrentAppointment] = useState<Appointment | null>(preSelectedAppointment || null);
  const [sessionFormat, setSessionFormat] = useState<'audio' | 'video'>('audio'); // Auto-detected from appointment

  // Patient selection
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(preSelectedPatient || null);
  const [sessionType, setSessionType] = useState<SessionMetadata['sessionType']>('therapy');
  const [appointmentReason, setAppointmentReason] = useState('');

  // Provider ID now from auth context (above)

  // Transcription state
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [fullTranscript, setFullTranscript] = useState('');
  const [transcriptionSession, setTranscriptionSession] = useState<TranscriptionSession | null>(null);

  // Filtering state
  const [filteringStats, setFilteringStats] = useState<FilteringStats | null>(null);

  // Clinical notes
  const [clinicalNotes, setClinicalNotes] = useState('');

  // Autosave state
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [lastSavedNotes, setLastSavedNotes] = useState<Date | null>(null);
  const [saveNotesError, setSaveNotesError] = useState<string | null>(null);

  // Recording data
  const recordingDataRef = useRef<{
    audioBlob: Blob;
    filteredBlob: Blob;
    metadata: any;
  } | null>(null);

  // Ref to AudioRecorder for programmatic stop
  const audioRecorderRef = useRef<AudioRecorderHandle>(null);

  // Flag: auto-start session as soon as patient is resolved (set when coming from appointment)
  const pendingAutoStartRef = useRef(false);
  // Prevents re-triggering auto-start for the same appointment after session ends
  const autoStartedAppointmentIdRef = useRef<string | null>(null);

  // When recording is auto-stopped by "End & Save Session", hold data needed to run pipeline
  const autoStopDataRef = useRef<{
    sessionId: string;
    appointmentId: string;
    patientId: string;
    providerName: string;
    patientName: string;
    savedPatientName: string;
    savedDuration: string;
    savedSessionType: string;
  } | null>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Session timer
  const [sessionDuration, setSessionDuration] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Post-session pipeline state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [pipelineResult, setPipelineResult] = useState<PostSessionResult | null>(null);
  const [pipelinePatientId, setPipelinePatientId] = useState<string>('');
  const [pipelinePatientName, setPipelinePatientName] = useState<string>('');
  const [pipelineAppointmentId, setPipelineAppointmentId] = useState<string>('');

  // Modal states
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [showCancelSessionModal, setShowCancelSessionModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load patients and check for active session
  useEffect(() => {
    const loadPatients = async () => {
      if (!providerId) return;
      const data = await FirebasePatientService.getAllPatients(providerId);
      setPatients(data);
      return data;
    };
    loadPatients().then(allPatients => {
      if (!allPatients) return;

      const session = sessionService.getActiveSession();
      if (session) {
        setActiveSession(session);
        setSessionMetadata(session.metadata);
        setClinicalNotes(session.clinicalNotes);

        // Find the patient
        const patient = allPatients.find((p: Patient) => p.id === session.metadata.patientId);
        if (patient) {
          setSelectedPatient(patient);
        }
      }
    });
  }, [providerId]);

  // Handle pre-selected appointment (auto-detect recording mode)
  useEffect(() => {
    if (preSelectedAppointment) {
      setCurrentAppointment(preSelectedAppointment);

      // Auto-detect session format based on appointment modality
      if (preSelectedAppointment.modality === 'video') {
        setSessionFormat('video');
      } else {
        setSessionFormat('audio');
      }

      // Pre-fill patient info if available
      if (preSelectedAppointment.patientId) {
        const patient = patients.find(p => p.id === preSelectedAppointment.patientId);
        if (patient) {
          setSelectedPatient(patient);
        }
      }

      // Pre-fill session details from appointment
      if (preSelectedAppointment.sessionType) {
        setSessionType(preSelectedAppointment.sessionType);
      }
      if (preSelectedAppointment.appointmentReason) {
        setAppointmentReason(preSelectedAppointment.appointmentReason);
      }

      // Auto-start session — only once per appointment, not again after session ends
      const apptId = preSelectedAppointment.id || preSelectedAppointment.patientId;
      if (preSelectedAppointment.patientId && !activeSession && autoStartedAppointmentIdRef.current !== apptId) {
        autoStartedAppointmentIdRef.current = apptId;
        pendingAutoStartRef.current = true;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedAppointment, patients]);

  // Auto-start when patient is ready (triggered by appointment flow)
  useEffect(() => {
    if (pendingAutoStartRef.current && selectedPatient && !activeSession) {
      pendingAutoStartRef.current = false;
      handleStartSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  // Session timer
  useEffect(() => {
    if (activeSession && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
    } else if (!activeSession && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setSessionDuration(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeSession]);

  // Autosave clinical notes every 30 seconds
  useEffect(() => {
    // Only autosave if there's an active session and notes have content
    if (!activeSession || !clinicalNotes || clinicalNotes.trim().length === 0) {
      return;
    }

    // Set up autosave timer (30 seconds after last change)
    const autosaveTimer = setTimeout(() => {
      saveNotesAutomatically();
    }, 30000); // 30 seconds

    return () => clearTimeout(autosaveTimer);
  }, [clinicalNotes, activeSession]);

  // Autosave function
  const saveNotesAutomatically = async () => {
    if (!activeSession || !clinicalNotes) {
      return;
    }

    setIsSavingNotes(true);
    setSaveNotesError(null);

    try {
      // Update notes in the session service
      sessionService.updateClinicalNotes(clinicalNotes);
      setLastSavedNotes(new Date());
    } catch (error: any) {
      console.error('Failed to autosave notes:', error);
      setSaveNotesError('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Start a new session
  const handleStartSession = async () => {
    if (!selectedPatient) {
      setError('Please select a patient');
      return;
    }

    try {
      setError(null);
      const metadata = await sessionService.startSession(
        selectedPatient.id,
        providerId,
        sessionType,
        appointmentReason || undefined,
        {
          onTranscriptionUpdate: (segment: TranscriptionSegment) => {
            setCurrentTranscript(segment.text);
            if (segment.isFinal && segment.text.trim()) {
              setFullTranscript(prev => prev + (prev ? ' ' : '') + segment.text.trim());
            }
          },
          onSessionUpdate: (session: TranscriptionSession) => {
            setTranscriptionSession(session);
            if (session.fullText) {
              setFullTranscript(session.fullText);
            }
          },
          onRecordingUpdate: (recording: boolean) => {
            setIsRecording(recording);
          },
          onError: (err: string) => {
            setError(err);
          },
          onFilteringUpdate: (segments: FilteredSegment[], stats: FilteringStats) => {
            setFilteringStats(stats);
          }
        },
        sessionFormat // Pass the detected session format
      );

      setSessionMetadata(metadata);
      const session = sessionService.getActiveSession();
      setActiveSession(session);

      // Link session to appointment if we have one
      if (currentAppointment && session) {
        await firebaseAppointmentService.startAppointment(currentAppointment.id, session.metadata.sessionId);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Handle recording complete
  const handleRecordingComplete = (
    audioBlob: Blob,
    filteredBlob: Blob,
    metadata: any,
    transcription?: TranscriptionSession
  ) => {
    recordingDataRef.current = { audioBlob, filteredBlob, metadata };

    if (activeSession) {
      sessionService.stopRecording(audioBlob, filteredBlob, metadata)
        .then(() => setActiveSession(sessionService.getActiveSession()))
        .catch(err => setError(err.message));
    }

    // If recording was auto-stopped by "End & Save Session", run pipeline now
    if (autoStopDataRef.current) {
      const data = autoStopDataRef.current;
      autoStopDataRef.current = null;
      const successMsg = `Session saved successfully!\n\nPatient: ${data.savedPatientName}\nDuration: ${data.savedDuration}\nType: ${data.savedSessionType}`;

      if (audioBlob && audioBlob.size > 1000) {
        setProcessingStatus('Transcribing session audio...');
        postSessionPipelineService.runPipeline(
          audioBlob, data.sessionId, data.appointmentId, providerId, data.patientId,
          'SOAP', undefined, data.providerName, data.patientName
        )
          .then(result => setPipelineResult(result))
          .catch(err => { console.error('Pipeline failed:', err); setShowSuccessModal(true); setSuccessMessage(successMsg); })
          .finally(() => { setIsProcessing(false); setProcessingStatus(''); });
      } else {
        setIsProcessing(false);
        setShowSuccessModal(true);
        setSuccessMessage(successMsg);
      }
    }
  };

  // End and save session — auto-stops recording if still running
  const handleEndSession = async () => {
    try {
      setError(null);
      if (!activeSession) throw new Error('No active session');

      const sessionData = await sessionService.endSession(clinicalNotes);

      if (currentAppointment) {
        await firebaseAppointmentService.completeAppointment(currentAppointment.id);

        // Send post-session follow-up notification (email + SMS)
        if (currentAppointment.patientId) {
          const aptDate = new Date(currentAppointment.appointmentDate);
          const dateStr = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate())
            .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
          NotificationTriggerService.getInstance().sendPostSession({
            patientId: currentAppointment.patientId,
            providerId,
            appointmentDate: dateStr,
            appointmentId: currentAppointment.id,
          }).catch(() => {});
        }
      }

      const savedPatientName = selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : sessionData.metadata.patientId;
      const savedDuration = formatDuration(sessionData.metadata.duration);
      const savedSessionType = sessionData.metadata.sessionType;
      const sessionId = sessionData.metadata.sessionId;
      const patientId = sessionData.metadata.patientId;
      const appointmentId = currentAppointment?.id || sessionId; // save before state reset
      const providerName = user?.displayName || 'Provider';
      const patientName = selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : 'Client';
      const audioBlob = recordingDataRef.current?.audioBlob; // capture before reset clears it

      // Store patient info for post-session review modal (before state reset clears them)
      setPipelinePatientId(patientId);
      setPipelinePatientName(patientName);
      setPipelineAppointmentId(appointmentId);

      // Reset session state
      setActiveSession(null);
      setSessionMetadata(null);
      setSelectedPatient(null);
      setCurrentTranscript('');
      setFullTranscript('');
      setTranscriptionSession(null);
      setFilteringStats(null);
      setClinicalNotes('');
      setAppointmentReason('');
      setCurrentAppointment(null);
      recordingDataRef.current = null;
      setShowEndSessionModal(false);

      if (isRecording) {
        // Auto-stop recording — pipeline runs in handleRecordingComplete when blob is ready
        autoStopDataRef.current = {
          sessionId, appointmentId, patientId,
          providerName, patientName,
          savedPatientName, savedDuration, savedSessionType,
        };
        setIsProcessing(true);
        setProcessingStatus('Finishing recording...');
        audioRecorderRef.current?.stopRecording();
        return;
      }
      const successMsg = `Session saved successfully!\n\nPatient: ${savedPatientName}\nDuration: ${savedDuration}\nType: ${savedSessionType}`;

      if (audioBlob && audioBlob.size > 1000) {
        setIsProcessing(true);
        setProcessingStatus('Transcribing session audio...');
        try {
          const result = await postSessionPipelineService.runPipeline(
            audioBlob, sessionId, appointmentId, providerId, patientId,
            'SOAP', undefined, providerName, patientName
          );
          setPipelineResult(result);
        } catch (err) {
          console.error('Pipeline failed:', err);
          setShowSuccessModal(true);
          setSuccessMessage(successMsg);
        } finally {
          setIsProcessing(false);
          setProcessingStatus('');
        }
      } else {
        setShowSuccessModal(true);
        setSuccessMessage(successMsg);
      }
    } catch (err: any) {
      setError(err.message);
      setShowEndSessionModal(false);
    }
  };

  // Cancel session
  const handleCancelSession = async () => {
    try {
      await sessionService.cancelSession();

      // Reset state
      setActiveSession(null);
      setSessionMetadata(null);
      setSelectedPatient(null);
      setIsRecording(false);
      setCurrentTranscript('');
      setFullTranscript('');
      setTranscriptionSession(null);
      setFilteringStats(null);
      setClinicalNotes('');
      setAppointmentReason('');
      setCurrentAppointment(null);
      recordingDataRef.current = null;

      // Close modal
      setShowCancelSessionModal(false);
    } catch (err: any) {
      setError(err.message);
      setShowCancelSessionModal(false);
    }
  };

  // Update clinical notes
  const handleNotesChange = (notes: string) => {
    setClinicalNotes(notes);
    // Clear any previous save errors when user starts typing
    if (saveNotesError) {
      setSaveNotesError(null);
    }
    // Immediate update to session service (for backwards compatibility)
    if (activeSession) {
      sessionService.updateClinicalNotes(notes);
    }
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          color: '#F7E9A0',
          margin: '0 0 8px 0'
        }}>
          Patient Sessions
        </h1>
        <p style={{ fontSize: '16px', color: '#e5e7eb', margin: 0 }}>
          Start and manage therapy sessions with live recording and transcription
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          background: '#EF4444',
          border: '1px solid #DC2626',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>Error</h3>
          <p style={{ margin: 0, color: '#fff' }}>{error}</p>
          <button
            onClick={() => setError(null)}
            style={{
              marginTop: '12px',
              background: '#DC2626',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Session Setup (when no active session) */}
      {!activeSession && (
        <div style={{
          background: '#28527A',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #6CA6C1'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#F7E9A0' }}>
            {currentAppointment ? 'Start Scheduled Appointment' : 'Start New Session'}
          </h2>

          {currentAppointment && (
            <div style={{
              background: '#16325B',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '20px',
              border: `2px solid ${sessionFormat === 'video' ? '#3b82f6' : '#10b981'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>
                  {sessionFormat === 'video' ? '📹' : '🎙️'}
                </span>
                <div>
                  <div style={{ fontWeight: '600', color: '#F7E9A0', fontSize: '16px' }}>
                    {sessionFormat === 'video' ? 'Video Appointment' : 'In-Person Appointment'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                    {new Date(currentAppointment.appointmentDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {currentAppointment.startTime}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '14px', color: '#e5e7eb' }}>
                <div><strong>Session Type:</strong> {currentAppointment.sessionType}</div>
                {currentAppointment.appointmentReason && (
                  <div style={{ marginTop: '4px' }}><strong>Reason:</strong> {currentAppointment.appointmentReason}</div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#e5e7eb', fontSize: '14px' }}>
                Select Patient *
              </label>
              <select
                value={selectedPatient?.id || ''}
                onChange={(e) => {
                  const patient = patients.find(p => p.id === e.target.value);
                  setSelectedPatient(patient || null);
                }}
                disabled={!!currentAppointment}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#16325B',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  opacity: currentAppointment ? 0.6 : 1,
                  cursor: currentAppointment ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">-- Select a patient --</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName} ({patient.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#e5e7eb', fontSize: '14px' }}>
                Session Type *
              </label>
              <select
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value as SessionMetadata['sessionType'])}
                disabled={!!currentAppointment}
                style={{
                  width: '100%',
                  opacity: currentAppointment ? 0.6 : 1,
                  cursor: currentAppointment ? 'not-allowed' : 'pointer',
                  padding: '10px',
                  background: '#16325B',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px'
                }}
              >
                <option value="initial-consultation">Initial Consultation</option>
                <option value="follow-up">Follow-up</option>
                <option value="therapy">Therapy</option>
                <option value="assessment">Assessment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {selectedPatient && (
            <div style={{
              background: '#16325B',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#F7E9A0', fontSize: '16px' }}>Patient Info</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                <div>
                  <span style={{ color: '#d1d5db' }}>Name:</span>{' '}
                  <span style={{ color: '#f9fafb' }}>{selectedPatient.firstName} {selectedPatient.lastName}</span>
                </div>
                <div>
                  <span style={{ color: '#d1d5db' }}>DOB:</span>{' '}
                  <span style={{ color: '#f9fafb' }}>{selectedPatient.dateOfBirth?.split('-').length === 3 ? `${selectedPatient.dateOfBirth.split('-')[1]}/${selectedPatient.dateOfBirth.split('-')[2]}/${selectedPatient.dateOfBirth.split('-')[0]}` : new Date(selectedPatient.dateOfBirth).toLocaleDateString('en-US')}</span>
                </div>
                <div>
                  <span style={{ color: '#d1d5db' }}>Email:</span>{' '}
                  <span style={{ color: '#f9fafb' }}>{selectedPatient.email}</span>
                </div>
                <div>
                  <span style={{ color: '#d1d5db' }}>Phone:</span>{' '}
                  <span style={{ color: '#f9fafb' }}>{selectedPatient.phone}</span>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e7eb', fontSize: '14px' }}>
              Appointment Reason (Optional)
            </label>
            <input
              type="text"
              value={appointmentReason}
              onChange={(e) => setAppointmentReason(e.target.value)}
              placeholder="e.g., Anxiety symptoms, medication review..."
              style={{
                width: '100%',
                padding: '10px',
                background: '#16325B',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Session Format Indicator */}
          {currentAppointment && (
            <div style={{
              background: sessionFormat === 'video' ? '#3b82f6' : '#10b981',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px',
              border: `2px solid ${sessionFormat === 'video' ? '#2563eb' : '#059669'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>
                  {sessionFormat === 'video' ? '📹' : '🎙️'}
                </span>
                <div>
                  <div style={{ fontWeight: '600', color: '#fff', fontSize: '14px' }}>
                    {sessionFormat === 'video' ? 'Video Session' : 'Audio Recording Session'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#e0e0e0' }}>
                    Appointment scheduled for {new Date(currentAppointment.appointmentDate).toLocaleDateString()} at {currentAppointment.startTime}
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleStartSession}
            disabled={!selectedPatient}
            style={{
              background: selectedPatient ? '#D4A017' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              color: selectedPatient ? '#113F67' : '#9ca3af',
              fontSize: '16px',
              fontWeight: '600',
              cursor: selectedPatient ? 'pointer' : 'not-allowed',
              opacity: selectedPatient ? 1 : 0.6
            }}
          >
            Start {sessionFormat === 'video' ? 'Video' : 'Audio'} Session with {selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : undefined || 'Patient'}
          </button>
        </div>
      )}

      {/* Active Session Display */}
      {activeSession && sessionMetadata && selectedPatient && (
        <>
          {/* Session Info */}
          <div style={{
            background: '#28527A',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            border: '2px solid #D4A017'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0', color: '#F7E9A0' }}>
                  {sessionFormat === 'video' ? '📹' : '🎙️'} Active Session: {selectedPatient.firstName} {selectedPatient.lastName}
                </h2>
                <div style={{ fontSize: '14px', color: '#e5e7eb' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: sessionFormat === 'video' ? '#3b82f6' : '#10b981',
                    color: '#fff',
                    fontWeight: '600',
                    marginRight: '8px'
                  }}>
                    {sessionFormat === 'video' ? 'Video' : 'Audio'}
                  </span>
                  <span>Type: {sessionMetadata.sessionType}</span>
                  <span style={{ margin: '0 12px', color: '#6CA6C1' }}>•</span>
                  <span>Duration: {formatDuration(sessionDuration)}</span>
                  {sessionMetadata.appointmentReason && (
                    <>
                      <span style={{ margin: '0 12px', color: '#6CA6C1' }}>•</span>
                      <span>Reason: {sessionMetadata.appointmentReason}</span>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowCancelSessionModal(true)}
                  style={{
                    background: '#EF4444',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel Session
                </button>
                <button
                  onClick={() => setShowEndSessionModal(true)}
                  style={{
                    background: '#10B981',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  End & Save Session
                </button>
              </div>
            </div>
          </div>

          {/* Audio Recorder */}
          <div style={{ marginBottom: '24px' }}>
            <AudioRecorder
              ref={audioRecorderRef}
              isActive={true}
              autoStart={true}
              sessionId={sessionMetadata.sessionId}
              patientId={sessionMetadata.patientId}
              appointmentReason={sessionMetadata.appointmentReason}
              sessionType={sessionMetadata.sessionType}
              onRecordingComplete={handleRecordingComplete}
              onRecordingStart={() => setIsRecording(true)}
              onRecordingStop={() => setIsRecording(false)}
              enableTranscription={false}
              enableVAD={true}
              enableContentFiltering={true}
            />
          </div>

          {/* Transcription Display */}
          {(fullTranscript || currentTranscript) && (
            <div style={{
              background: '#28527A',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #6CA6C1'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#F7E9A0' }}>Session Transcript</h3>
              <div style={{
                background: '#16325B',
                borderRadius: '6px',
                padding: '16px',
                maxHeight: '300px',
                overflowY: 'auto',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#f9fafb',
                whiteSpace: 'pre-wrap'
              }}>
                {fullTranscript || currentTranscript || 'Listening for speech...'}
              </div>

              {transcriptionSession && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '12px',
                  color: '#d1d5db',
                  display: 'flex',
                  gap: '16px'
                }}>
                  <span>Segments: {transcriptionSession.segments.length}</span>
                  <span>Words: {transcriptionSession.totalWords}</span>
                  <span>Avg Confidence: {Math.round((transcriptionSession.avgConfidence || 0) * 100)}%</span>
                </div>
              )}
            </div>
          )}

          {/* Content Filtering Stats */}
          {filteringStats && (
            <div style={{
              background: '#28527A',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #6CA6C1'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#F7E9A0' }}>Content Filtering</h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px'
              }}>
                <div style={{ background: '#16325B', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>
                    {filteringStats.relevantSegments}
                  </div>
                  <div style={{ fontSize: '12px', color: '#d1d5db' }}>Relevant</div>
                </div>

                <div style={{ background: '#16325B', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>
                    {filteringStats.filteredSegments}
                  </div>
                  <div style={{ fontSize: '12px', color: '#d1d5db' }}>Filtered</div>
                </div>

                <div style={{ background: '#16325B', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3B82F6' }}>
                    {filteringStats.medicalContent}
                  </div>
                  <div style={{ fontSize: '12px', color: '#d1d5db' }}>Medical</div>
                </div>

                <div style={{ background: '#16325B', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#D4A017' }}>
                    {Math.round(filteringStats.averageRelevanceScore * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: '#d1d5db' }}>Avg Score</div>
                </div>
              </div>
            </div>
          )}

          {/* Clinical Notes */}
          <div style={{
            background: '#28527A',
            borderRadius: '8px',
            padding: '20px',
            border: '1px solid #6CA6C1'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#F7E9A0' }}>Clinical Notes</h3>
              <VoiceInputButton onResult={text => handleNotesChange(clinicalNotes + ' ' + text)} />
            </div>
            <textarea
              value={clinicalNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Enter clinical notes, observations, treatment plans, and follow-up instructions..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '12px',
                background: '#16325B',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />

            {/* Autosave Status Indicator */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px'
            }}>
              <div style={{
                fontSize: '12px',
                color: '#d1d5db'
              }}>
                Notes will be saved with the session recording and transcript
              </div>

              <div style={{
                fontSize: '13px',
                fontWeight: '500',
                padding: '4px 8px',
                borderRadius: '4px',
                minWidth: '150px',
                textAlign: 'right'
              }}>
                {isSavingNotes && (
                  <span style={{ color: '#6CA6C1' }}>
                    Saving...
                  </span>
                )}
                {!isSavingNotes && lastSavedNotes && !saveNotesError && (
                  <span style={{ color: '#10B981' }}>
                    Last saved at {lastSavedNotes.toLocaleTimeString()}
                  </span>
                )}
                {saveNotesError && (
                  <span style={{ color: '#EF4444' }}>
                    {saveNotesError}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* End Session Confirmation Modal */}
      {showEndSessionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #6CA6C1',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#F7E9A0', fontSize: '1.5rem' }}>
              End & Save Session?
            </h3>
            <p style={{ color: '#e5e7eb', marginBottom: '24px', lineHeight: '1.6' }}>
              Are you sure you want to end this session? The session will be saved to the patient's record including:
            </p>
            <ul style={{ color: '#d1d5db', marginBottom: '24px', paddingLeft: '20px' }}>
              <li>Audio recording (original & filtered)</li>
              <li>Transcripts (original & filtered)</li>
              <li>Clinical notes</li>
              <li>Session metadata</li>
            </ul>
            {selectedPatient && (
              <div style={{
                background: '#28527A',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '24px',
                border: '1px solid #6CA6C1'
              }}>
                <div style={{ color: '#F7E9A0', fontSize: '14px', marginBottom: '4px' }}>
                  Patient: <strong>{selectedPatient.firstName} {selectedPatient.lastName}</strong>
                </div>
                <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                  Duration: {formatDuration(sessionDuration)} • Type: {sessionType}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEndSessionModal(false)}
                style={{
                  background: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEndSession}
                style={{
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                End & Save Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Session Confirmation Modal */}
      {showCancelSessionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #EF4444',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#EF4444', fontSize: '1.5rem' }}>
              ⚠️ Cancel Session?
            </h3>
            <p style={{ color: '#e5e7eb', marginBottom: '16px', lineHeight: '1.6' }}>
              Are you sure you want to cancel this session? <strong style={{ color: '#EF4444' }}>This action cannot be undone.</strong>
            </p>
            <p style={{ color: '#d1d5db', marginBottom: '24px', fontSize: '14px' }}>
              All data from this session will be permanently lost:
            </p>
            <ul style={{ color: '#d1d5db', marginBottom: '24px', paddingLeft: '20px', fontSize: '14px' }}>
              <li>Audio recordings</li>
              <li>Transcripts</li>
              <li>Clinical notes</li>
              <li>Session metadata</li>
            </ul>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCancelSessionModal(false)}
                style={{
                  background: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Keep Session
              </button>
              <button
                onClick={handleCancelSession}
                style={{
                  background: '#EF4444',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Yes, Cancel Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing overlay */}
      {isProcessing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '1px solid #6CA6C1'
          }}>
            <div style={{
              width: '50px', height: '50px',
              border: '4px solid #374151', borderTop: '4px solid #D4A017',
              borderRadius: '50%', animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <h3 style={{ color: '#F7E9A0', margin: '0 0 8px' }}>Processing Session</h3>
            <p style={{ color: '#d1d5db', margin: 0 }}>{processingStatus}</p>
          </div>
        </div>
      )}

      {/* Post-session review modal (AI notes + transcript) */}
      {pipelineResult && (
        <PostSessionReviewModal
          result={pipelineResult}
          appointmentId={pipelineAppointmentId}
          patientId={pipelinePatientId}
          patientName={pipelinePatientName}
          providerId={providerId}
          onClose={() => setPipelineResult(null)}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #10B981',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#10B981', fontSize: '1.5rem' }}>
              ✓ Session Saved Successfully!
            </h3>
            <p style={{ color: '#e5e7eb', marginBottom: '24px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
              {successMessage}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSuccessModal(false)}
                style={{
                  background: '#10B981',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderSessionTab;
